// Staff check-ins in the manager's language. Each check-in is translated once per language
// (the translate-checkins function caches it on the row), so this is cheap after the first view.
import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import { parseCheckin, type Checkin } from '@/src/lib/checkin';
import type { NightCheckin } from '@/src/hooks/useEodNight';

export interface CheckinTranslation { from: string | null; answers: Record<string, unknown>; checkin: Checkin }

export function useCheckinTranslations(checkins: NightCheckin[], target: string | null) {
  const lang = target || 'en';
  const [map, setMap] = useState<Record<string, CheckinTranslation>>({});
  const [busy, setBusy] = useState(false);
  const asked = useRef(new Set<string>());

  useEffect(() => {
    // Already translated (cached on the row), or nothing to do.
    const next: Record<string, CheckinTranslation> = {};
    const need: string[] = [];
    for (const r of checkins) {
      const cached = r.translations?.[lang];
      if (cached) next[r.id] = { ...cached, checkin: parseCheckin(cached.answers) };
      else if (!asked.current.has(`${lang}:${r.id}:${r.updatedAt ?? r.submittedAt}`)) need.push(r.id);
    }
    setMap((m) => ({ ...m, ...next }));
    if (!need.length) return;
    for (const r of checkins) if (need.includes(r.id)) asked.current.add(`${lang}:${r.id}:${r.updatedAt ?? r.submittedAt}`);
    setBusy(true);
    void supabase.functions.invoke('translate-checkins', { body: { report_ids: need, target: lang } }).then(({ data }) => {
      const t = (data as { translations?: Record<string, { from: string | null; answers: Record<string, unknown> }> } | null)?.translations ?? {};
      setMap((m) => {
        const out = { ...m };
        for (const [id, v] of Object.entries(t)) out[id] = { ...v, checkin: parseCheckin(v.answers) };
        return out;
      });
      setBusy(false);
    });
  }, [checkins, lang]);

  return { translations: map, busy };
}
